import { Building2, ChartLine, CreditCard, Gauge, LayoutDashboard, LifeBuoy, Receipt, ScrollText, Server, Settings, Tags, UserCircle, Users } from 'lucide-react';
import { Outlet, useNavigate } from 'react-router';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { PLATFORM_ROLE_LABEL } from '@barbeiropro/contracts';
import { AccountLink, AppShell, SidebarNav, UserMenuShell, useAppTheme, type ShellNavGroup } from '@/app/shell';
import { usePlatformMe } from '@/features/auth/guards';
import { useMe } from '@/lib/session';

const GROUPS: ShellNavGroup[] = [
  {
    label: 'Visão geral',
    items: [
      { to: '/plataforma', label: 'Visão geral', icon: LayoutDashboard },
      { to: '/plataforma/metricas', label: 'Métricas', icon: ChartLine },
    ],
  },
  {
    label: 'Clientes',
    items: [
      { to: '/plataforma/organizacoes', label: 'Organizações', icon: Building2 },
      { to: '/plataforma/usuarios', label: 'Usuários', icon: Users },
      { to: '/plataforma/assinaturas', label: 'Assinaturas', icon: CreditCard },
      { to: '/plataforma/planos', label: 'Planos', icon: Tags },
      { to: '/plataforma/cobranca', label: 'Cobrança', icon: Receipt },
      { to: '/plataforma/uso', label: 'Uso e IA', icon: Gauge },
      { to: '/plataforma/suporte', label: 'Suporte', icon: LifeBuoy },
    ],
  },
  {
    label: 'Sistema',
    items: [
      { to: '/plataforma/auditoria', label: 'Auditoria', icon: ScrollText },
      { to: '/plataforma/servicos', label: 'Serviços', icon: Server },
      { to: '/plataforma/configuracoes', label: 'Configurações', icon: Settings },
    ],
  },
];

function PlatformNav({ onNavigate }: { onNavigate?: () => void }) {
  return <SidebarNav groups={GROUPS} rootTo="/plataforma" ariaLabel="Plataforma" onNavigate={onNavigate} />;
}

function PlatformAccount({ onNavigate }: { onNavigate?: () => void }) {
  const { data: me } = useMe();
  if (!me) return null;
  return <AccountLink to="/plataforma/conta" onNavigate={onNavigate} email={me.user.email} label="Minha conta" />;
}

function PlatformTitle() {
  return (
    <div className="flex min-w-0 items-center gap-2 px-2 sm:px-3">
      <span className="truncate font-display text-base font-semibold">Plataforma</span>
      <span className="shrink-0 rounded-full bg-brass/15 px-2 py-0.5 text-xs font-medium text-amber-ink">Super admin</span>
    </div>
  );
}

function PlatformUserMenu() {
  const { data: me } = useMe();
  const { data: role } = usePlatformMe();
  const navigate = useNavigate();
  if (!me) return null;
  return (
    <UserMenuShell
      name={me.user.name}
      email={me.user.email}
      roleLine={role ? `Plataforma: ${PLATFORM_ROLE_LABEL[role]}` : null}
    >
      <DropdownMenuItem onSelect={() => navigate('/plataforma/conta')}>
        <UserCircle className="size-4" aria-hidden /> Minha conta
      </DropdownMenuItem>
    </UserMenuShell>
  );
}

export function PlatformLayout() {
  useAppTheme();
  return (
    <AppShell
      brandTo="/plataforma"
      renderNav={(close) => <PlatformNav onNavigate={close} />}
      renderAccount={(close) => <PlatformAccount onNavigate={close} />}
      headerStart={<PlatformTitle />}
      headerEnd={<PlatformUserMenu />}
    >
      <Outlet />
    </AppShell>
  );
}
